import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

export type CustomerFeatures = Record<string, boolean>;

interface CustomerFeaturesState {
  features: CustomerFeatures;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches tenant-level feature flags for a specific customer.
 * Uses the dashboard features endpoint via the Core API.
 */
export function useCustomerFeatures(customerId: string | undefined) {
  const [state, setState] = useState<CustomerFeaturesState>({
    features: {},
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!customerId) return;

    let cancelled = false;

    const load = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiService.get<{
          customerId: string;
          subscriptionTier: string | null;
          features: { featureKey: string; enabled: boolean }[];
        }>(`/dashboard/customers/${customerId}/features`);

        if (cancelled) return;

        const map: CustomerFeatures = {};
        if (response.data?.features) {
          for (const f of response.data.features) {
            map[f.featureKey] = f.enabled;
          }
        }
        setState({ features: map, loading: false, error: null });
      } catch (err: any) {
        if (cancelled) return;
        // Fail-open: don't block the partner from seeing tabs
        setState({ features: {}, loading: false, error: err?.message || 'Failed to load features' });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [customerId]);

  const isFeatureEnabled = (featureKey: string): boolean => {
    // Fail-closed: if loading failed, hide gated features until we can confirm entitlement
    if (state.error) return false;
    // If key isn't present in the response, default to enabled (ungated)
    if (!(featureKey in state.features)) return true;
    return state.features[featureKey];
  };

  return { ...state, isFeatureEnabled };
}
